/**
 * The API contract.
 *
 * This is deliberately the shape from plan.md's data model and endpoint table,
 * not whatever the fixtures find convenient (D-21). `infra/lambda/backend/
 * services/types.ts` gets the same shapes at task 16. When task 22 swaps the
 * fixture client for `fetch`, nothing here should have to move.
 */

/* ---- Creator-owned data (§12, NFR-13) --------------------------------- */

/** A game or show. Owns its franchise AND its card tint (D-8 superseded). */
export type Game = {
  id: string;
  name: string;
  franchise: string;
  tint: string;
  /**
   * Public-bucket key for this game's cover art — `game-cover-art` FR-1.
   *
   * Absent means no game art, which is every game until one is uploaded. Its
   * tabs then fall through to the sprite exactly as they always have.
   */
  coverKey?: string;
  /** Retire, never delete (D-12). Retired games leave the pickers; assets keep them. */
  retired: boolean;
};

export type Tag = {
  id: string;
  label: string;
  retired: boolean;
};

/** Read-only, from Patreon. Never invented locally (§11.4). */
export type Tier = {
  id: string;
  name: string;
  /** What defines "and above" (D-5). Never display order. */
  amountCents: number;
  patrons: number;
};

export type FieldDef = {
  id: string;
  label: string;
  kind: "text" | "mono" | "pips" | "duration";
};

export type SlotDef = {
  id: string;
  label: string;
  required: boolean;
  /** File extensions, for the wrong-type message (§13.2). */
  accept: string[];
  /** For the form sub-line; omitted on cover art and thumbnails (D-15). */
  shortName?: string;
  /**
   * Bytes. Absent means the global cap — see `checkUpload`, which also applies
   * a tighter default to image slots (DE-D4).
   */
  maxBytes?: number;
  /**
   * Retired slots take no new uploads; files already stored stay downloadable
   * and gated (`editable-file-slots` FR-13). Absent means active (FS-D2).
   */
  retired?: boolean;
};

/** An asset type owns its fields, its file slots and its card object (D-10). */
export type AssetTypeDef = {
  type: string;
  /** Plural, for lists: 'Tabs'. */
  label: string;
  /** Singular, for screen titles: 'Tab' -> "NEW TAB" (D-15). */
  headingNoun: string;
  cardObject: "SHEET" | "CASSETTE" | "CRT" | "QBLOCK";
  fields: FieldDef[];
  slots: SlotDef[];
  retired: boolean;
};

export type Creator = {
  creatorId: string;
  /**
   * Where unsigned public files live — `asset-cover-art` DE-D3.
   *
   * Environment configuration on a domain object, which is not where it
   * belongs on principle. It is here because the bucket name is constructed by
   * CDK, so a `VITE_` build variable would mean the workflow hardcoding a name
   * the CDK owns — and the failure when those drift is every cover 404ing,
   * which looks exactly like "no covers uploaded".
   *
   * Optional: absent in any environment deployed before covers shipped.
   */
  publicAssetsBaseUrl?: string;
  campaignId: string;
  displayName: string;
  logoKey: string;
  /** The tab count is computed, never authored (§12). */
  landingCopy: { headline: string; cadence: string };
  tiers: Tier[];
  /** Authored, keyed by Patreon tier id (D-9). */
  tierBlurbs: Record<string, string>;
  tiersSyncedAt: string;
  games: Game[];
  tags: Tag[];
  assetTypes: AssetTypeDef[];
  /** '{type}:{slot}' -> tierId -> granted. The matrix key is the pair (D-7). */
  matrix: Record<string, Record<string, boolean>>;
  /**
   * Published, non-deleted assets. Only the public payload carries it - the
   * landing needs a count without being allowed a listing.
   */
  assetCount?: number;
};

/** §11.2 / D-7: the matrix is keyed on the (type, slot) pair, not on either alone. */
export const matrixKeyFor = (assetType: string, slotId: string): string =>
  `${assetType}:${slotId}`;

/* ---- Assets ------------------------------------------------------------ */

export type StoredFile = {
  key: string;
  name: string;
  size: number;
  contentType: string;
  /** Read from the PDF at upload, never typed (D-11). */
  pages?: number;
};

export type Asset = {
  id: string;
  creatorId: string;
  slug: string;
  /** Renames keep permanent redirects (D-11). */
  slugHistory: string[];
  assetType: string;
  title: string;
  /** Franchise AND tint derive from this. Neither is stored on the asset. */
  gameId: string;
  tags: string[];
  note: string;
  performanceUrl?: string;
  status: "draft" | "published";
  /** Set at FIRST publish, then editable. Drafts have none. */
  addedAt?: string;
  /** Tier id; means that tier and above. */
  minTier: string;
  /** Declared by the asset type. */
  fields: Record<string, unknown>;
  /** Keyed by the type's slot ids. */
  files: Record<string, StoredFile>;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * What `GET /assets` returns: an asset the caller may see, plus **per-slot**
 * grants resolved server-side. D-7 - a tier can be entitled to the PDF and not
 * the MusicXML on the same asset, so a single boolean on the asset is wrong.
 */
export type PatronAsset = Asset & {
  grants: Record<string, boolean>;
};

/* ---- Session ----------------------------------------------------------- */

export type Me = {
  sub: string;
  name: string;
  avatarUrl?: string;
  /** Absent if the identity provider did not give us one. */
  email?: string;
  /** null when the viewer is signed in but backs no tier. */
  tier: Tier | null;
  isOwner: boolean;
};
